/* eslint-disable complexity */
import { FormEvent, useState } from "react";

import { useAnalytics } from "../../components";
import {
    BxpButton,
    LinkButton,
    getAppearance,
    getProminence,
    getSpacing,
    useIdentityUrl
} from "../../components";
import { UiEmailSignupModel } from "../../models";
import { MapModelToProps } from "../../utils";

import { Form, InputEmail } from "./UiEmailSignup.styles";

declare global {
    interface Window {
        grecaptcha: {
            ready: (callback: () => void) => void;
            execute: (
                siteKey: string,
                options: { action: string }
            ) => Promise<string>;
        };
    }
}

export type UiEmailSignupProps = MapModelToProps<UiEmailSignupModel>;

const UiEmailSignup = ({ model }: UiEmailSignupProps) => {
    const { sendTrackEvent } = useAnalytics();
    const {
        buttonLabel,
        buttonText,
        constrainInputWidth,
        emailLabel,
        entryTitle,
        hideEmailInput,
        theme
    } = model.content;
    const [email, setEmail] = useState("");

    const signupUrl = useIdentityUrl({
        email
    });
    const trackEvent = () => {
        sendTrackEvent("submittedUiEmailSignup", {
            event: "submitted",
            eventComponent: model.type,
            eventContainer: "content",
            href: "/signup_redirect",
            label: buttonText,
            labelEnglish: entryTitle,
            uuid: model.id
        });
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        trackEvent();

        const recaptcha_key = window.location.href?.includes("trello.com")
            ? "6Le9VxMnAAAAALKRlj8jmKwUYmFTZWcF0y4o9QeD"
            : "6LcIg3QnAAAAAPmTkAv_ZanJY5dRDUJo-i5hr_aN";

        // Only attempt reCAPTCHA if the script has loaded
        if (typeof window !== "undefined" && window.grecaptcha) {
            try {
                await new Promise<void>((resolve) => {
                    window.grecaptcha.ready(() => resolve());
                });
                await window.grecaptcha.execute(recaptcha_key, {
                    action: "trello_submit"
                });
            } catch (error) {
                console.error("reCAPTCHA error:", error);
                // Continue with signup even if reCAPTCHA fails
            }
        }

        location.assign(signupUrl);
    };

    // BXPT-1430: Remove link to AA signup link from <form> after experimentation is completed
    return (
        <Form
            action={signupUrl}
            data-testid="ui-email-signup-form"
            data-uuid={model.id}
            onSubmit={handleSubmit}
            hideEmailInput={hideEmailInput}
        >
            {hideEmailInput ? (
                <LinkButton
                    appearance={theme?.content?.appearance}
                    prominence={theme?.content?.prominence}
                    style={theme?.content?.style}
                    aria-label={buttonLabel}
                    onClick={trackEvent}
                    url={signupUrl}
                >
                    {buttonText}
                </LinkButton>
            ) : (
                <>
                    <InputEmail
                        aria-label={emailLabel}
                        $bxpProminence={getProminence(
                            theme?.content?.prominence,
                            theme?.content?.style
                        )}
                        $isWidthConstrained={constrainInputWidth}
                        data-testid="ui-email-signup-input"
                        name="email"
                        onChange={(e) => setEmail(e.currentTarget.value)}
                        placeholder={emailLabel}
                        type="email"
                    />
                    <BxpButton
                        $bxpAppearance={getAppearance(
                            theme?.content?.appearance,
                            theme?.content?.style
                        )}
                        $bxpProminence={getProminence(
                            theme?.content?.prominence,
                            theme?.content?.style
                        )}
                        $bxpSpacing={getSpacing()}
                    >
                        <button type="submit" aria-label={buttonLabel}>
                            {buttonText}
                        </button>
                    </BxpButton>
                </>
            )}
        </Form>
    );
};

export default UiEmailSignup;
